import {
  type AudienceCreationFlowSnapshot,
  deriveBuildStatusFromFlowSnapshot,
  deriveRouteAckContextMaterialsFromFlowSnapshot,
  deriveAudienceImportStatusFromFlowSnapshot,
} from '@digital-twins/shared-types'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import type {
  AudienceImportContextMaterialsProjection,
  AudienceImportPreviewProjection,
  AudienceImportRouteAck,
  AudienceImportSession,
  AudienceImportStatusProjection,
  AudienceImportThinRouteAck,
  AudienceImportWorkbookProjection,
  IdentityStepShell,
} from '@/types/components/audiences/import-session'

type AudienceImportAnyRouteAck =
  | AudienceImportRouteAck
  | AudienceImportThinRouteAck

/** Minimal shape required for routeAck merge. Thin acks omit statusProjection. */
function isValidRouteAck(ack: unknown): ack is { flowSnapshot: unknown; machineVersion?: unknown } {
  return (
    ack != null &&
    typeof ack === 'object' &&
    (ack as { flowSnapshot?: unknown }).flowSnapshot != null &&
    typeof (ack as { flowSnapshot?: unknown }).flowSnapshot === 'object'
  )
}

/** Get expiresAt from statusProjection or, for thin acks, from flowSnapshot. */
function getExpiresAt(
  ack: { flowSnapshot?: { expiresAt?: string | null } | null; statusProjection?: { expiresAt?: string | null } | null },
  current: { expiresAt?: string | null } | null | undefined,
): string | null | undefined {
  const fromStatus = ack.statusProjection?.expiresAt
  if (fromStatus !== undefined && fromStatus !== null) return fromStatus
  const fromFlow = ack.flowSnapshot?.expiresAt
  if (fromFlow !== undefined && fromFlow !== null) return fromFlow
  return current?.expiresAt
}

function getStatusProjection(
  ack: AudienceImportAnyRouteAck,
): AudienceImportStatusProjection | null | undefined {
  return 'statusProjection' in ack ? ack.statusProjection : undefined
}

export function getFlowSnapshotMachineVersion(
  snapshot: Pick<AudienceCreationFlowSnapshot, 'machineVersion'> | null | undefined,
): number {
  return typeof snapshot?.machineVersion === 'number' &&
    Number.isFinite(snapshot.machineVersion)
    ? snapshot.machineVersion
    : 0
}

export function isSameOrNewerFlowSnapshot(
  candidate: Pick<AudienceCreationFlowSnapshot, 'machineVersion'> | null | undefined,
  current: Pick<AudienceCreationFlowSnapshot, 'machineVersion'> | null | undefined,
): boolean {
  return getFlowSnapshotMachineVersion(candidate) >= getFlowSnapshotMachineVersion(current)
}

export function preferNewerFlowSnapshot<
  T extends Pick<AudienceCreationFlowSnapshot, 'machineVersion'> | null | undefined,
>(current: T, candidate: T): T {
  if (!candidate) {
    return (candidate ?? current) as T
  }
  if (!current) {
    return candidate
  }

  return isSameOrNewerFlowSnapshot(candidate, current) ? candidate : current
}

export function getRouteAckFromImportSessionEvent(
  event: Pick<ImportSessionUpdatedEvent, 'routeAck'>,
): AudienceImportAnyRouteAck | null {
  return event.routeAck ?? null
}

export function mergeRouteAckIntoStatusProjection(
  current: AudienceImportStatusProjection | null | undefined,
  ack: AudienceImportAnyRouteAck,
): AudienceImportStatusProjection | null | undefined {
  const sp = getStatusProjection(ack)
  if (sp) {
    return sp
  }
  return current ?? undefined
}

/**
 * Extract routeAck from a 409 Conflict error response when the backend
 * enriches the response with authoritative state for immediate recovery.
 * Returns null for malformed or thin-incomplete routeAck so callers fall back to refetch.
 */
export function getRouteAckFromConflictError(
  error: unknown,
): AudienceImportAnyRouteAck | null {
  const data = (error as { response?: { data?: { routeAck?: unknown } } })
    ?.response?.data
  const ack = data?.routeAck
  return isValidRouteAck(ack) ? (ack as AudienceImportAnyRouteAck) : null
}

/**
 * Merges routeAck into preview projection. When ack includes previewProjection
 * (e.g. shuffle, preview_twin_enriched), uses it directly. Otherwise merges
 * flowSnapshot/contextMaterials. Backend shuffle currently omits previewProjection,
 * so shuffle events still trigger a refetch until backend adds mergeable delta.
 */
export function mergeRouteAckIntoPreviewProjection(
  current: AudienceImportPreviewProjection | null | undefined,
  ack: AudienceImportAnyRouteAck,
  options?: { audienceName?: string },
): AudienceImportPreviewProjection | null | undefined {
  if (!isValidRouteAck(ack)) return current ?? undefined
  const currentVersion = current?.machineVersion ?? 0
  if (typeof ack.machineVersion === 'number' && ack.machineVersion < currentVersion) {
    return current ?? undefined
  }
  if (ack.previewProjection) {
    return ack.previewProjection
  }

  if (!current) {
    return current
  }

  try {
    const statusProj = getStatusProjection(ack)
    const updatedAt = statusProj?.updatedAt ?? current.updatedAt
    const expiresAt = getExpiresAt(ack, current)
    const contextMaterials = {
      ...current.contextMaterials,
      ...deriveRouteAckContextMaterialsFromFlowSnapshot(ack.flowSnapshot),
    }
    return {
      ...current,
      machineVersion: ack.machineVersion,
      updatedAt,
      expiresAt: expiresAt ?? current.expiresAt,
      audienceDraft: options?.audienceName
        ? {
            ...current.audienceDraft,
            name: options.audienceName,
          }
        : current.audienceDraft,
      contextMaterials,
      flowSnapshot: ack.flowSnapshot,
    }
  } catch {
    return current ?? undefined
  }
}

export function mergeRouteAckIntoWorkbookProjection(
  current: AudienceImportWorkbookProjection | null | undefined,
  ack: AudienceImportAnyRouteAck,
  options?: {
    audienceName?: string
    sheetAssignment?: AudienceImportWorkbookProjection['sheetAssignment']
  },
): AudienceImportWorkbookProjection | null | undefined {
  if (!isValidRouteAck(ack)) return current ?? undefined
  const currentVersion = current?.machineVersion ?? 0
  if (typeof ack.machineVersion === 'number' && ack.machineVersion < currentVersion) {
    return current ?? undefined
  }
  if (!current) {
    return current
  }

  try {
    const statusProj = getStatusProjection(ack)
    const expiresAt = getExpiresAt(ack, current)
    return {
      ...current,
      machineVersion: ack.machineVersion,
      updatedAt: statusProj?.updatedAt ?? current.updatedAt,
      expiresAt: expiresAt ?? current.expiresAt,
      audienceDraft: options?.audienceName
        ? {
            ...current.audienceDraft,
            name: options.audienceName,
          }
        : current.audienceDraft,
      sheetAssignment: options?.sheetAssignment ?? current.sheetAssignment,
      flowSnapshot: ack.flowSnapshot,
    }
  } catch {
    return current ?? undefined
  }
}

export function mergeRouteAckIntoContextMaterialsProjection(
  current: AudienceImportContextMaterialsProjection | null | undefined,
  ack: AudienceImportAnyRouteAck,
  options?: { audienceName?: string },
): AudienceImportContextMaterialsProjection | null | undefined {
  if (!isValidRouteAck(ack)) return current ?? undefined
  const currentVersion = current?.machineVersion ?? 0
  if (typeof ack.machineVersion === 'number' && ack.machineVersion < currentVersion) {
    return current ?? undefined
  }
  if (!current) {
    return current
  }

  try {
    const statusProj = getStatusProjection(ack)
    const expiresAt = getExpiresAt(ack, current)
    const contextMaterials = {
      ...current.contextMaterials,
      ...deriveRouteAckContextMaterialsFromFlowSnapshot(ack.flowSnapshot),
    }
    return {
      ...current,
      machineVersion: ack.machineVersion,
      updatedAt: statusProj?.updatedAt ?? current.updatedAt,
      expiresAt: expiresAt ?? current.expiresAt,
      audienceDraft: options?.audienceName
        ? {
            ...current.audienceDraft,
            name: options.audienceName,
          }
        : current.audienceDraft,
      contextMaterials,
      flowSnapshot: ack.flowSnapshot,
    }
  } catch {
    return current ?? undefined
  }
}

export function mergeRouteAckIntoSession(
  current: AudienceImportSession | null | undefined,
  ack: AudienceImportAnyRouteAck,
): AudienceImportSession | null | undefined {
  if (!isValidRouteAck(ack)) return current ?? undefined
  const currentVersion = current?.machineVersion ?? 0
  if (typeof ack.machineVersion === 'number' && ack.machineVersion < currentVersion) {
    return current ?? undefined
  }
  if (!current) {
    return current
  }

  try {
    const statusProj = getStatusProjection(ack)
    const expiresAt = getExpiresAt(ack, current)
    return {
      ...current,
      machineVersion: ack.machineVersion,
      status: deriveAudienceImportStatusFromFlowSnapshot(ack.flowSnapshot),
      updatedAt: statusProj?.updatedAt ?? current.updatedAt,
      expiresAt: expiresAt ?? current.expiresAt,
      buildStatus: deriveBuildStatusFromFlowSnapshot(ack.flowSnapshot),
      contextMaterials: {
        ...current.contextMaterials,
        ...deriveRouteAckContextMaterialsFromFlowSnapshot(ack.flowSnapshot),
      },
    }
  } catch {
    return current ?? undefined
  }
}

/**
 * Merge lightweight progress/status projection into cached session.
 * Used when polling during active analyze/create instead of full session GET.
 */
export function mergeStatusProjectionIntoSession(
  current: AudienceImportSession | null | undefined,
  projection: AudienceImportStatusProjection,
): AudienceImportSession | null | undefined {
  if (
    !projection ||
    typeof projection !== 'object' ||
    projection.flowSnapshot == null ||
    typeof projection.flowSnapshot !== 'object'
  ) {
    return current ?? undefined
  }
  const currentVersion = current?.machineVersion ?? 0
  if (
    typeof projection.machineVersion === 'number' &&
    projection.machineVersion < currentVersion
  ) {
    return current ?? undefined
  }
  if (!current) {
    return current
  }

  try {
    return {
      ...current,
      machineVersion: projection.machineVersion,
      status: deriveAudienceImportStatusFromFlowSnapshot(projection.flowSnapshot),
      updatedAt: projection.updatedAt ?? current.updatedAt,
      expiresAt: projection.expiresAt ?? current.expiresAt,
      buildStatus: deriveBuildStatusFromFlowSnapshot(projection.flowSnapshot),
      contextMaterials: {
        ...current.contextMaterials,
        ...deriveRouteAckContextMaterialsFromFlowSnapshot(projection.flowSnapshot),
      },
    }
  } catch {
    return current ?? undefined
  }
}

export function mergeRouteAckIntoIdentityShell(
  current: IdentityStepShell | null | undefined,
  ack: AudienceImportAnyRouteAck,
): IdentityStepShell | null | undefined {
  if (!isValidRouteAck(ack)) return current ?? undefined
  const currentVersion = current?.machineVersion ?? 0
  if (typeof ack.machineVersion === 'number' && ack.machineVersion < currentVersion) {
    return current ?? undefined
  }
  if (!current) {
    return current
  }

  try {
    const preview = ack.flowSnapshot?.branchSummaries?.preview
    const previewState = preview?.state
    const nextPreviewSync =
      previewState === 'queued' || previewState === 'running'
        ? {
            status: 'refreshing' as const,
            message: 'Preview refresh is queued.',
            canRefresh: false,
          }
        : preview?.requiresRefresh
          ? {
              status: 'stale' as const,
              message:
                'Preview artifact is out of date. Refresh it if you want regenerated preview cards before creating the audience.',
              canRefresh: false,
            }
          : current.previewSync
            ? {
                status: 'fresh' as const,
                message:
                  current.previewSync.status === 'fresh'
                    ? current.previewSync.message
                    : 'Preview artifact is current with the latest saved identity decisions.',
                canRefresh: false,
              }
            : current.previewSync

    const statusProj = getStatusProjection(ack)
    const expiresAt = getExpiresAt(ack, current)
    return {
      ...current,
      machineVersion: ack.machineVersion,
      status: deriveAudienceImportStatusFromFlowSnapshot(ack.flowSnapshot),
      updatedAt: statusProj?.updatedAt ?? current.updatedAt,
      expiresAt: expiresAt ?? current.expiresAt,
      buildStatus: deriveBuildStatusFromFlowSnapshot(ack.flowSnapshot),
      contextMaterials: {
        ...current.contextMaterials,
        ...deriveRouteAckContextMaterialsFromFlowSnapshot(ack.flowSnapshot),
      },
      previewSync: nextPreviewSync,
    }
  } catch {
    return current ?? undefined
  }
}
